import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { inflate } from 'zlib';

import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';
import { 
  allRfidProductionFields, 
  allRfidAggregatedConsumptionFields 
} from '../../ngrx/graphql';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class RfidProductionService {
  constructor(
    private authService: AuthenticationService,
    private credentialService: CredentialService,
    private store: Store<any>
  ) {}

  private invokeLambda(payload: any): Observable<any> {
    return new Observable(observer => {
      this.credentialService.lambda.invoke({
        FunctionName: environment.magentoFunction, // Adjust to the correct function name
        Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
          observer.error(err);
        } else {
          const output = typeof data.Payload === 'string'
            ? JSON.parse(data.Payload)
            : data.Payload;
          if (output.gzip) {
            const options = {
              windowBits: 15
            };
            inflate(new Buffer(output.body, 'base64'), options, (err, data) => {
              if (err) {
                observer.error(err.stack);
              } else {
                const unzippedOutput = Object.assign({}, output, {
                  body: data.toString()
                });
                observer.next(unzippedOutput);
                observer.complete();
              }
            });
          } else {
            observer.next(output);
            observer.complete();
          }
        }
      });
    });
  }

  private invokeMutation(payload: any): Observable<any> {
    return this.invokeLambda(payload);
  }

  createRfidProduction(rfidProduction: any): Observable<any> {
    const variables = { rfidProduction };
    
    const query = `mutation($rfidProduction: RfidProductionInput!) {
      createRfidProduction(rfidProduction: $rfidProduction) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  updateRfidProduction(rfidProduction: any): Observable<any> {
    const variables = { rfidProduction };
    
    const query = `mutation($rfidProduction: RfidProductionInput!) {
      updateRfidProduction(rfidProduction: $rfidProduction) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  /**
   * Records paper sheet consumption and ensures RFID production record exists
   * @param jobId The job ID
   * @param job The job object containing details to create production record if needed 
   * @param consumption Paper consumption details
   */
  recordPaperSheetConsumption(
    jobId: string,
    job: any,
    consumption: {
      front_sheets_used: number,
      back_sheets_used: number,
      front_sheets_scrap?: number,
      back_sheets_scrap?: number,
      scrap_record?: {
        scrap_category: string,
        scrap_code: string,
        scrap_reason: string,
        job_status: string,
        front_sheets_scrap?: number,
        back_sheets_scrap?: number,
        inlay_sheets_scrap?: number,
        front_laminate_scrap?: number,
        back_laminate_scrap?: number
      }
    }
  ): Observable<any> {
    const variables = {
      jobId,
      job,
      consumption
    };
    
    const query = `mutation($jobId: String!, $job: RFIDJobInput!, $consumption: PaperSheetConsumptionInput!) {
      recordPaperSheetConsumption(
        jobId: $jobId,
        job: $job,
        consumption: $consumption
      ) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  /**
   * Records inlay sheet consumption and ensures RFID production record exists
   * @param jobId The job ID
   * @param job The job object containing details to create production record if needed
   * @param consumption Inlay consumption details
   */
  recordInlaySheetConsumption(
    jobId: string,
    job: any,
    consumption: {
      inlay_sheets_used: number,
      inlay_sheets_scrap?: number,
      inlay_part_number?: string,
      scrap_record?: {
        scrap_category: string,
        scrap_code: string,
        scrap_reason: string,
        job_status: string,
        front_sheets_scrap?: number,
        back_sheets_scrap?: number,
        inlay_sheets_scrap?: number,
        front_laminate_scrap?: number,
        back_laminate_scrap?: number
      }
    }
  ): Observable<any> {
    const variables = {
      jobId,
      job,
      consumption
    };
    
    const query = `mutation($jobId: String!, $job: RFIDJobInput!, $consumption: InlaySheetConsumptionInput!) {
      recordInlaySheetConsumption(
        jobId: $jobId,
        job: $job,
        consumption: $consumption
      ) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  /**
   * Records laminate consumption and ensures RFID production record exists
   * @param jobId The job ID
   * @param job The job object containing details to create production record if needed
   * @param consumption Laminate consumption details
   */
  recordLaminateConsumption(
    jobId: string,
    job: any,
    consumption: {
      front_laminate_feet_used: number,
      back_laminate_feet_used: number,
      front_laminate_scrap?: number,
      back_laminate_scrap?: number,
      laminate_type?: string,
      scrap_record?: {
        scrap_category: string,
        scrap_code: string,
        scrap_reason: string,
        job_status: string,
        front_sheets_scrap?: number,
        back_sheets_scrap?: number,
        inlay_sheets_scrap?: number,
        front_laminate_scrap?: number,
        back_laminate_scrap?: number
      }
    }
  ): Observable<any> {
    const variables = {
      jobId,
      job,
      consumption
    };
    
    const query = `mutation($jobId: String!, $job: RFIDJobInput!, $consumption: LaminateConsumptionInput!) {
      recordLaminateConsumption(
        jobId: $jobId,
        job: $job,
        consumption: $consumption
      ) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  /**
   * Add a scrap record independently
   * @param jobId The job ID
   * @param scrapRecord Scrap record details
   */
  addScrapRecord(
    jobId: string,
    scrapRecord: {
      scrap_category: string,
      scrap_code: string,
      scrap_reason: string,
      job_status: string,
      front_sheets_scrap?: number,
      back_sheets_scrap?: number,
      inlay_sheets_scrap?: number,
      front_laminate_scrap?: number,
      back_laminate_scrap?: number
    }
  ): Observable<any> {
    const variables = {
      jobId,
      scrapRecord
    };
    
    const query = `mutation($jobId: String!, $scrapRecord: ScrapRecordInput!) {
      addScrapRecord(
        jobId: $jobId,
        scrapRecord: $scrapRecord
      ) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  /**
   * Get scrap records for a job
   * @param jobId The job ID
   */
  getScrapRecordsForJob(jobId: string): Observable<any> {
    const variables = { jobId };
    
    const query = `query($jobId: String!) {
      getScrapRecordsForJob(jobId: $jobId) {
        id
        scrap_category
        scrap_code
        scrap_reason
        job_status
        front_sheets_scrap
        back_sheets_scrap
        inlay_sheets_scrap
        front_laminate_scrap
        back_laminate_scrap
        timestamp
      }
    }`;

    return this.invokeMutation({ query, variables });
  }

  getRfidProduction(jobId: string): Observable<any> {
    const query = `{
      getRfidProduction(jobId: "${jobId}") {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeLambda({ query });
  }

  getRfidProductionByOrderId(orderId: string): Observable<any> {
    const query = `{
      getRfidProductionByOrderId(orderId: "${orderId}") {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeLambda({ query });
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/rfid-production.service.ts